import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { Table, Tag, Row, Col, Input, Button, FloatButton, Typography, Card, Tooltip } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { FaCheck, FaTimes } from "react-icons/fa";
import JutgeColors from "../../../../styles/JutgeColors";
import { GetJutgeProblemesId } from "../../../../dao/jutge/GetJutgeProblemesId";
import { FaEye } from "react-icons/fa";
import { BsEye, BsCheck } from "react-icons/bs";
import { SearchOutlined } from "@mui/icons-material";
import RevisioModal from "../../activitatsId/content/RevisioModal";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { BiRefresh } from "react-icons/bi";
import { GetJutgeProblemesIdAlumnes } from "../../../../dao/jutge/GetJutgeProblemesIdAlumnes";
import ProblemaIdEstadistiques from "./ProblemaIdEstadistiques";
import ModoDesafioTag from "../../activitatsId/content/ModoDesafioTag";

interface ProblemaIdAlumnesProps {
    propIdProblema?: string;
    propIdActivitat?: string;
    isModal?: boolean;
}

const ProblemaIdAlumnes: React.FC<ProblemaIdAlumnesProps> = ({ propIdProblema, propIdActivitat, isModal }) => {
    const { isLoggedIn } = useContext(AuthContext);
    const params = useParams();
    const id_problema = propIdProblema || params.id_problema;
    const id_activitat = propIdActivitat || params.id_activitat;
    const [dades, setDades] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [selected, setSelected] = useState<any>();

    // estado para controlar qué fila expandida está en la subtabla
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");

    const handleSearch = (selectedKeys: string, confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    useEffect(() => {
        if (isLoggedIn) {
            checkDades();
        }
    }, [id_problema]);

    async function checkDades() {
        setLoading(true);
        if (id_activitat === "0") {
            const students = await GetJutgeProblemesIdAlumnes(id_problema + "");
            setDades({ enviaments: students }); // Wrap in object to match grouping logic or adjust grouping
        } else {
            const dad = await GetJutgeProblemesId(id_problema + "", id_activitat || "0");
            setDades(dad);
        }
        setLoading(false);
    }

    // 👉 función para agrupar los enviaments por usuario
    function groupByUser(enviaments: any[]) {
        if (id_activitat === "0") {
            // Already grouped by student from the new procedure
            return enviaments.map(item => ({
                id_usuari: item.id_usuari,
                alumne: item.usuari,
                correctes: item.be,
                incorrectes: item.malament,
                enviaments: [], // details will be loaded via ProblemaIdEstadistiques
                progres: item.progres
            }));
        }

        const grouped: Record<string, any[]> = {};
        if (enviaments && enviaments.length > 0) {
            for (const item of enviaments) {
                if (!grouped[item.id_usuari]) {
                    grouped[item.id_usuari] = [];
                }
                grouped[item.id_usuari].push(item);
            }

            return Object.entries(grouped).map(([id_usuari, items]) => {
                const correctes = items.filter(i => i.estat === 1).length;
                const incorrectes = items.filter(i => i.estat !== 1).length;
                // origen de l'alumne (mateix per a totes les seves respostes): agafem el primer definit
                const origenItem = items.find(i => i.origen !== undefined && i.origen !== null);
                const total = correctes + incorrectes;
                return {
                    id_usuari,
                    alumne: items[0].alumne,
                    correctes,
                    incorrectes,
                    origen: origenItem ? origenItem.origen : null,
                    resolt: total > 0 && incorrectes === 0,
                    enviaments: items,
                };
            });
        } else {
            return [];
        }
    }

    interface ColumnFilterItem {
        text?: React.ReactNode;
        value?: string | number | boolean;
        children?: ColumnFilterItem[];
    }

    // Inicial i color determinista per a l'avatar de l'alumne
    const avatarColor = (nom: string) => {
        const palette = ['#527e4c', '#285a4a', '#377a37', '#6d8261', '#9ab383', '#1890ff', '#722ed1', '#eb2f96'];
        let hash = 0;
        for (let i = 0; i < (nom || '').length; i++) hash = (nom.charCodeAt(i) + ((hash << 5) - hash)) | 0;
        return palette[Math.abs(hash) % palette.length];
    };

    // columnas tabla principal (1 fila por usuario)
    const mainColumns: ColumnsType<any> = [
        {
            key: 'alumne',
            title: 'Alumne',
            dataIndex: 'alumne',
            render: (nom, record) => {
                const inicial = (nom || '?').trim().charAt(0).toUpperCase();
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: avatarColor(nom), color: '#fff', fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15
                        }}>{inicial}</div>
                        <div>
                            <Typography.Text strong>{nom}</Typography.Text>
                            <div style={{ fontSize: 11, color: '#999' }}>ID: {record.id_usuari}</div>
                        </div>
                    </div>
                );
            },
            filters: dades?.enviaments
                ? Array.from(new Set<string>(dades?.enviaments?.map((e: any) => e.alumne || e.usuari))).map(al => ({
                    text: al,
                    value: al,
                }))
                : [],
            filterSearch: (input: string, record: ColumnFilterItem) => {
                const text = record.text?.toString() || '';
                return normalizeString(text).includes(normalizeString(input));
            },
            onFilter: (value, record) =>
                normalizeString(record.alumne).includes(normalizeString(value as string)),
        },
        {
            key: 'resolt',
            title: 'Estat',
            align: 'center',
            hidden: id_activitat === "0",
            render: (_, record) => (
                record.resolt
                    ? <Tag color="success" icon={<FaCheck />} style={{ borderRadius: 12, padding: '2px 10px' }}>Resolt</Tag>
                    : <Tag color="default" style={{ borderRadius: 12, padding: '2px 10px' }}>Pendent</Tag>
            )
        },
        {
            key: 'progres',
            title: 'Progrés',
            dataIndex: 'progres',
            hidden: id_activitat !== "0",
            render: (progres) => {
                const val = Number(progres);
                let color = val >= 80 ? 'green' : val >= 50 ? 'blue' : val >= 25 ? 'orange' : 'red';
                return <Tag color={color} style={{ fontWeight: 'bold' }}>{val.toFixed(2)}%</Tag>;
            }
        },
        {
            key: 'mode',
            title: 'Mode',
            align: 'center',
            hidden: id_activitat === "0",
            render: (_, record) => <ModoDesafioTag origen={record.origen} />
        },
        {
            key: 'stats',
            title: 'Tests',
            align: 'center',
            render: (_, record) => {
                const total = record.correctes + record.incorrectes;
                return (
                    <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <Tooltip title="Correctes">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#389e0d', fontWeight: 600 }}>
                                <FaCheck /> {record.correctes}
                            </span>
                        </Tooltip>
                        <span style={{ color: '#d9d9d9' }}>/</span>
                        <Tooltip title="Incorrectes">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: record.incorrectes > 0 ? '#cf1322' : '#bbb', fontWeight: 600 }}>
                                <FaTimes /> {record.incorrectes}
                            </span>
                        </Tooltip>
                        <span style={{ color: '#bbb', fontSize: 12 }}>({total})</span>
                    </div>
                );
            }
        },
        {
            key: 'actions',
            dataIndex: 'actions',
            align: 'right',
            render: (e, ele) => (
                <Tooltip title="Visualitzar entregues">
                    <Button
                        onClick={() => checkElement(ele)}
                        style={{ backgroundColor: JutgeColors.colors.color3, color: '#fff', border: 'none' }}
                        shape="circle"
                        icon={<FaEye />}
                    />
                </Tooltip>
            ),
        },
    ];

    // columnas subtabla (detalle de enviaments por usuario)
    const subColumns: ColumnsType<any> = [

        {
            key: 'ordre',
            title: 'Test',
            dataIndex: 'ordre',
            sorter: (a, b) => a.ordre - b.ordre, // 👈 define cómo ordenar
            defaultSortOrder: 'ascend',          // 👈 siempre empieza de menor a mayor
            render: (e) => (
                <Tag color={JutgeColors.colors.color3}>
                    Test #{e}
                </Tag>
            )
        },
        {
            key: 'moment',
            title: 'Moment',
            dataIndex: 'moment',
            sorter: (a, b) => new Date(a.moment).getTime() - new Date(b.moment).getTime(),
            defaultSortOrder: 'descend',
            render: (e) => <Tag color={JutgeColors.colors.color2}>{e.substring(0, e.lastIndexOf("-"))}</Tag>
        },
        {
            key: 'methodname',
            title: 'Mètode',
            dataIndex: 'methodname',
            render: (e) => (e ? <Tag color={"purple"}>{e}</Tag> : <></>)
        },
        {
            key: 'estat',
            title: 'Estat',
            dataIndex: 'estat',
            render: (e) => (
                <Tag color={e === 1 ? JutgeColors.colors.color4 : JutgeColors.colors.color1}>
                    {e === 1 ? "CORRECTE" : "INCORRECTE"}
                </Tag>
            )
        },
        {
            key: 'entrada',
            title: 'Entrada',
            dataIndex: 'entrada',
            render: (value: string, ele) => {
                try {
                    const parts = value.split(":");
                    const decoded = atob(parts[2]); // 👈 decodifica base64
                    return <pre style={{ whiteSpace: "pre-wrap" }}>{decoded}</pre>;
                } catch (err) {
                    return (ele.tipus !== 'method' ? <Tag color="red">Error al decodificar</Tag> : <Tag color="red">n/a</Tag>);
                }
            }
        }

    ];

    // subtabla: expandable con actual vs expected
    // subtabla: expandable con error + actual vs expected
    const nestedTableProps: TableProps<any> = {
        expandable: {
            expandedRowRender: (record) => {
                // 🔹 decodificar actual, expected y error
                let formattedActual = "";
                let formattedExpected = "";
                let formattedError = "";

                try {
                    formattedActual = atob(record?.actual?.split(":")[2] || "").replace(/\n/g, "\n");
                } catch {
                    formattedActual = "Error al decodificar 'actual'";
                }

                try {
                    formattedExpected = atob(record?.expected?.split(":")[2] || "").replace(/\n/g, "\n");
                } catch {
                    formattedExpected = "Error al decodificar 'expected'";
                }

                try {
                    if (record?.errors) {
                        formattedError = atob(record?.errors.split(":")[2] || "").replace(/\n/g, "\n");
                    }
                } catch {
                    formattedError = "Error al decodificar 'error'";
                }

                return (
                    <>
                        {/* 🔴 Franja roja de error */}
                        {formattedError && formattedError.trim() !== "" && (
                            <div
                                style={{
                                    backgroundColor: "#ffe6e6",
                                    borderLeft: "4px solid #ff4d4f",
                                    color: "#7a0019",
                                    padding: "10px",
                                    marginBottom: "10px",
                                    borderRadius: "8px",
                                    whiteSpace: "pre-wrap",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                }}
                            >
                                <strong>Error:</strong>
                                <br />
                                {formattedError}
                            </div>
                        )}

                        {/* 🧩 Actual vs Expected */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <h5>Actual:</h5>
                                <pre style={{ whiteSpace: "pre-wrap" }}>{formattedActual}</pre>
                            </Col>
                            <Col span={12}>
                                <h5>Expected:</h5>
                                <pre style={{ whiteSpace: "pre-wrap" }}>{formattedExpected}</pre>
                            </Col>
                        </Row>
                    </>
                );
            },
            expandIcon: ({ expanded, onExpand, record }) => {
                const isExpanded = expandedRowKeys.includes(record.moment);
                return record.estat === 1 ? (
                    <BsCheck />
                ) : (
                    <BsEye
                        style={{
                            cursor: "pointer",
                            fontSize: "18px",
                            color: isExpanded ? JutgeColors.colors.color4 : "inherit",
                        }}
                        onClick={(e: any) => {
                            if (isExpanded) {
                                setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.moment));
                            } else {
                                setExpandedRowKeys([record.moment]); // solo uno abierto a la vez
                            }
                            onExpand(record, e);
                        }}
                    />
                );
            },
            expandedRowKeys,
        },
    };


    function checkElement(ele: any) {
        setLoading(true);
        const ele2 = { ...ele, id: id_problema, id_practica: id_activitat }
        setSelected(ele2);
        setModal(true);
    }

    function closeModal() {
        setModal(false);
        setLoading(false);
    }

    function normalizeString(str: string) {
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // elimina tildes
            .toLowerCase();
    }

    function reload() {
        setLoading(true);
        checkDades();
    }

    return (
        <div style={isModal ? { padding: 0 } : { padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            <style>{`
                .pia-row-odd td { background: #fafbfc; }
                .pia-row-even td, .pia-row-odd td { transition: background 0.15s; }
                .pia-row-even:hover td, .pia-row-odd:hover td { background: #eef5ec !important; }
            `}</style>
            {isModal && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Button
                        icon={<BiRefresh />}
                        onClick={() => reload()}
                        loading={loading}
                        style={{ background: AvaluacioColors.colors.color6, borderColor: AvaluacioColors.colors.color6, color: '#fff' }}
                    >
                        Refrescar
                    </Button>
                </div>
            )}
            {!isModal && (
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Typography.Title level={2} style={{ marginBottom: 0 }}>{dades?.nom}</Typography.Title>
                        <Typography.Text type="secondary">Seguiment de l'exercici</Typography.Text>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Button
                            type="primary"
                            icon={<BiRefresh />}
                            onClick={() => reload()}
                            loading={loading}
                            style={{ background: AvaluacioColors.colors.color6, borderColor: AvaluacioColors.colors.color6 }}
                        >
                            Refrescar
                        </Button>
                    </div>
                </div>
            )}

            <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}>
                <Table
                    size="middle"
                    bordered={false}
                    loading={loading}
                    columns={mainColumns}
                    dataSource={dades ? groupByUser(dades.enviaments) : []}
                    rowKey="id_usuari"
                    pagination={{ pageSize: 12, hideOnSinglePage: true }}
                    getPopupContainer={() => document.body}
                    rowClassName={(_, index) => index % 2 === 0 ? 'pia-row-even' : 'pia-row-odd'}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ padding: "10px 30px", background: '#fafafa' }}>
                                {id_activitat === "0" ? (
                                    <ProblemaIdEstadistiques
                                        userId={record.id_usuari}
                                        idProblem={id_problema}
                                        idActivity="0"
                                    />
                                ) : (
                                    <Table
                                        size="small"
                                        columns={subColumns}
                                        dataSource={record.enviaments}
                                        rowKey="moment"
                                        pagination={false}
                                        {...nestedTableProps}
                                    />
                                )}
                            </div>
                        )
                    }}
                />
            </Card>
            {modal && <RevisioModal element={selected} visible={modal} onClose={closeModal} />}
        </div>
    );
};

export default ProblemaIdAlumnes;
